import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import BackButton from "@/components/Common/BackButton";
import { PageWrapper } from "@/components/PageWrapper";
import {
  PropertyWantAdListing,
  SelectDropDown,
  MatchesSkeleton,
} from "@/components";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import { getMatches } from "@/services/v2/matches";
import { getMlsListingByKey } from "@/services/v2/mlsListings";
import {
  favouriteWantAd,
  unFavouriteWantAd,
} from "@/services/v2/wantAdService";
import { pageURLs } from "@/constants";

const SORT_OPTIONS = [
  { value: "most_relevant", label: "Most relevant" },
  { value: "most_recent", label: "Most recent" },
];

const PAGE_LIMIT = 20;

const buildAddressLine1 = (d) => {
  if (!d) return "";
  if (d.UnparsedAddress) return d.UnparsedAddress;
  return [d.StreetNumber, d.StreetName, d.StreetSuffix, d.UnitNumber]
    .filter(Boolean)
    .join(" ");
};

const buildAddressLine2 = (d) => {
  if (!d) return "";
  const cs = [d.City, d.StateOrProvince].filter(Boolean).join(", ");
  return [cs, d.PostalCode].filter(Boolean).join(" ");
};

// React Query key for an MLS listing's want-listing matches. listingKey is the
// stable identity; sortType is appended at the call site so the optimistic
// favorite update can match every sort variant via a partial key.
const MLS_MATCHES_KEY = "mlsListingMatches";

const MlsListingMatches = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listingKey } = useParams();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const queryClient = useQueryClient();

  const stateAddress = location.state?.address || "";
  const stateCityStateZip = location.state?.cityStateZip || "";

  const [address, setAddress] = useState(stateAddress);
  const [cityStateZip, setCityStateZip] = useState(stateCityStateZip);
  const [sortType, setSortType] = useState(SORT_OPTIONS[0].value);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: [MLS_MATCHES_KEY, listingKey, sortType],
    enabled: !!listingKey,
    queryFn: async ({ pageParam = 1 }) => {
      // MLS reverse-lookup uses a lower score threshold (20) because MLS-feed
      // listings score lower than user-authored Inventory — see useMatches.js.
      const response = await getMatches({
        mlsListingKey: listingKey,
        page: pageParam,
        limit: PAGE_LIMIT,
        sortType,
        minScore: 20,
      });
      return response?.data || {};
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.hasNextPage ? allPages.length + 1 : undefined,
    select: (result) => ({
      ...result,
      mlsMatches: result.pages.flatMap((p) => p?.data || []),
      totalMlsMatches: result.pages[0]?.totalCount || 0,
    }),
  });

  const mlsMatches = data?.mlsMatches ?? [];
  const totalMlsMatches = data?.totalMlsMatches ?? 0;

  // Favorite/unfavorite with optimistic cache update so the heart fill flips
  // instantly without waiting for a refetch.
  const favoriteMutation = useMutation({
    mutationFn: ({ isFavourite, payload }) =>
      isFavourite ? unFavouriteWantAd(payload) : favouriteWantAd(payload),
    onMutate: async ({ wantAdId, isFavourite }) => {
      await queryClient.cancelQueries({
        queryKey: [MLS_MATCHES_KEY, listingKey],
      });
      const previous = queryClient.getQueriesData({
        queryKey: [MLS_MATCHES_KEY, listingKey],
      });
      queryClient.setQueriesData(
        { queryKey: [MLS_MATCHES_KEY, listingKey] },
        (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((p) => ({
              ...p,
              data: (p?.data || []).map((wa) =>
                wa.Want_ad_Id === wantAdId
                  ? { ...wa, IsFavourite: isFavourite ? 0 : 1 }
                  : wa,
              ),
            })),
          };
        },
      );
      return { previous };
    },
    onError: (err, vars, context) => {
      // Roll back the optimistic flip
      context?.previous?.forEach(([key, value]) =>
        queryClient.setQueryData(key, value),
      );
    },
  });

  // Defense-in-depth: redirect off default Gitcha
  useEffect(() => {
    if (isGitchaDefaultWebsite === true) {
      navigate(pageURLs.home, { replace: true });
    }
  }, [isGitchaDefaultWebsite, navigate]);

  // Address fallback for deep-link case (no router state)
  useEffect(() => {
    if (stateAddress || !listingKey) return;
    let cancelled = false;
    getMlsListingByKey(listingKey)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || null;
        if (data) {
          setAddress(buildAddressLine1(data));
          setCityStateZip(buildAddressLine2(data));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [listingKey, stateAddress]);

  const handleSortChange = (selected) => {
    if (selected?.value) {
      setSortType(selected.value);
    }
  };

  const handleShowMore = () => fetchNextPage();

  const handleWantAdItemClick = (wantAdId, slug) => {
    sessionStorage.setItem(
      "mlsListingMatchesScrollY",
      String(window.scrollY),
    );
    navigate(`${pageURLs.wantAdDetails}/${slug || wantAdId}`);
  };

  const handleFavoriteOrUnfavoriteWantAd = (
    IsFavourite,
    Want_ad_Id,
    clientId = [],
  ) => {
    if (!Want_ad_Id) return;
    const payload = {
      clientId: IsFavourite === false ? clientId : null,
      wantAdId: Want_ad_Id,
    };
    favoriteMutation.mutate(
      { isFavourite: IsFavourite, wantAdId: Want_ad_Id, payload },
      {
        onSuccess: (response) => {
          if (response?.errorCode !== 0) return;
          let message = "";
          if (clientId.length > 0) {
            message =
              "Want-Listing has been favorited for " +
              clientId.length +
              (clientId.length > 1 ? " clients" : " client");
          } else {
            message =
              IsFavourite === true
                ? "Want-Listing has been removed from your favorites"
                : "Want-Listing has been added to your favorites";
          }
          toast.success(message, { autoClose: 1000 });
        },
      },
    );
  };

  const hasNoData = !mlsMatches || mlsMatches.length === 0;
  const headerAddress = address || "this listing";

  return (
    <PageWrapper>
      <div className="mls-listing-matches">
        <BackButton />

        <div className="mls-listing-matches__header">
          <h1>Potential Buyers for {headerAddress}</h1>
          {cityStateZip && (
            <div className="mls-listing-matches__sub-address">
              {cityStateZip}
            </div>
          )}
        </div>

        <div className="sort-items-panel">
          <div className="sort-items-inner-wrapper">
            <div className="row">
              <div className="d-flex mb-4">
                <div className="col">
                  <h2 className="member-inventory-details-subtitle">
                    {totalMlsMatches}{" "}
                    {totalMlsMatches === 1 ? "Match" : "Matches"} to
                    Want-Listings
                  </h2>
                </div>
                <div style={{ width: "auto !important" }}>
                  <div className="d-flex select-wrap position-relative gap-3">
                    <Form.Label style={{ fontSize: 15 }}>Sort By:</Form.Label>
                    <SelectDropDown
                      id="mls-matches-sort"
                      name="mls-matches-sort"
                      options={SORT_OPTIONS}
                      className="basic-single sort-select d-inline-block mb-0"
                      classNamePrefix="select"
                      placeholder="Select"
                      value={SORT_OPTIONS.find((o) => o.value === sortType)}
                      onChange={handleSortChange}
                      errorClass="d-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isLoading && hasNoData ? (
          <MatchesSkeleton />
        ) : hasNoData ? (
          <div className="no-result-wrap">
            <h3>No matches yet</h3>
            <p>
              No active Want-Listings match this listing yet. The matcher will
              surface new buyers as Want-Listings are posted.
            </p>
          </div>
        ) : (
          <>
            {mlsMatches.map((wa, index) => (
              <PropertyWantAdListing
                list={wa}
                key={`${wa?.wantAdId || index}`}
                handleFavoriteOrUnfavoriteWantAd={
                  handleFavoriteOrUnfavoriteWantAd
                }
                handleWantAdItemClick={handleWantAdItemClick}
              />
            ))}
            {hasNextPage && (
              <div className="text-center pt-3 pb-3 w-100">
                <button
                  className="btn btn-primary"
                  onClick={handleShowMore}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? "Loading..." : "Show More"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default MlsListingMatches;
